// Shared API helpers: backend base URL + Bearer-token-aware fetch.
//
// The backend issues an opaque session token on login (see
// portalbackend/Backend/auth_guard.php) that must be sent back as
// `Authorization: Bearer <token>` on any endpoint that calls
// require_portal_session(). This file is the one place that reads/writes
// that token so every page uses the same storage key and header shape.

export const API_BASE = 'https://dev.sliitleo.org/Backend';

const TOKEN_KEY = 'portal_token';

// public/service-worker.js's `pushsubscriptionchange` handler needs the
// session token to re-POST a rotated push subscription to
// save_push_subscription.php, but a service worker runs in a separate
// storage context and cannot read localStorage. Mirroring into IndexedDB
// (readable from both contexts) here -- the one place the token is ever
// written/cleared -- means every other call site in the app needs no
// changes at all. Best-effort: if IndexedDB is unavailable (private
// browsing, etc.), the mirror silently fails and the service worker just
// falls back to no-op, same as before this existed.
const IDB_NAME = 'leo-portal';
const IDB_STORE = 'auth';
const IDB_KEY = 'portal_token';

function idbWriteToken(token) {
  try {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(IDB_STORE)) req.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => {
      const db = req.result;
      const tx = db.transaction(IDB_STORE, 'readwrite');
      if (token) tx.objectStore(IDB_STORE).put(token, IDB_KEY);
      else tx.objectStore(IDB_STORE).delete(IDB_KEY);
      tx.oncomplete = () => db.close();
    };
    req.onerror = () => {};
  } catch {
    // IndexedDB unavailable -- nothing to do, mount-time resubscribe in
    // App.js remains the fallback path.
  }
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || '';
}

export function setToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    idbWriteToken(token);
  }
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  idbWriteToken(null);
}

/**
 * fetch() wrapper that adds the Bearer token (when present) and a default
 * JSON content-type for non-GET requests. Usage is identical to fetch().
 */
export function authFetch(url, options = {}) {
  const token = getToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  return fetch(url, { ...options, headers });
}

/**
 * Tell the server to destroy this browser's session token. Must run BEFORE
 * clearToken()/localStorage.clear(), because it reads the token from there.
 * Fire-and-forget: logging out must never wait on, or fail because of, the
 * network. keepalive lets the request finish even as the page navigates away.
 */
export function serverLogout() {
  const token = getToken();
  if (!token) return;
  try {
    fetch(`${API_BASE}/logout.php`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Ignore -- the local logout still proceeds.
  }
}

/** Read the currently logged-in user's role/id from localStorage (or null). */
export function getCurrentUser() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user && typeof user === 'object' ? user : null;
  } catch {
    return null;
  }
}
